import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import Modal from '../components/Modal';
import { Btn } from '../components/Input';
import {
  IconCamera, IconUser, IconWrench, IconAirplane, IconNote, IconHome,
  IconClock, IconLocation, IconX, IconCalendar, IconPlus,
} from '../components/Icons';
import { Skeleton } from '../components/Skeleton';
import EmptyState from '../components/EmptyState';
import HelpHint from '../components/HelpHint';

// ─── Constants ────────────────────────────────────────────────────────────────

const TYPY = {
  sesja:     { Icon: IconCamera,   color: 'var(--acc)',  bg: 'rgba(184,84,40,0.15)'   },
  spotkanie: { Icon: IconUser,     color: 'var(--ok2)',  bg: 'rgba(90,158,114,0.15)'  },
  serwis:    { Icon: IconWrench,   color: '#7a6e9e',     bg: 'rgba(122,110,158,0.15)' },
  wyjazd:    { Icon: IconAirplane, color: '#4a9eba',     bg: 'rgba(74,158,186,0.15)'  },
  inne:      { Icon: IconNote,     color: 'var(--ink3)', bg: 'rgba(160,160,170,0.15)' },
  spacer:    { Icon: IconHome,     color: 'var(--acc)',  bg: 'rgba(184,84,40,0.10)'   },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function toDateOnly(dt) {
  if (!dt) return '';
  return dt.slice(0, 10);
}

function formatTime(dt) {
  if (!dt || dt.length <= 10) return '';
  return dt.slice(11, 16);
}

function isoWeekStart(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1 - day);
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function dateToLocalISO(d) {
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
}

// ─── ZdarzenieModal ──────────────────────────────────────────────────────────

function ZdarzenieModal({ zdarzenie, klienci, onSave, onDelete, onClose }) {
  const { t } = useTranslation();
  const isEdit = !!zdarzenie?.id;

  const [form, setForm] = useState({
    typ: zdarzenie?.typ || 'sesja',
    tytul: zdarzenie?.tytul || '',
    data_od: zdarzenie?.data_od || '',
    data_do: zdarzenie?.data_do || '',
    klient_id: zdarzenie?.klient_id || '',
    osoba_kontakt: zdarzenie?.osoba_kontakt || '',
    firma_kontakt: zdarzenie?.firma_kontakt || '',
    telefon_kontakt: zdarzenie?.telefon_kontakt || '',
    adres: zdarzenie?.adres || '',
    notatki: zdarzenie?.notatki || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleKlientChange = (klientId) => {
    set('klient_id', klientId);
    if (!klientId) return;
    const k = klienci.find(k => String(k.id) === String(klientId));
    if (!k) return;
    if (k.imie_nazwisko) set('osoba_kontakt', k.imie_nazwisko);
    if (k.nazwa_firmy)   set('firma_kontakt', k.nazwa_firmy);
    if (k.telefon)       set('telefon_kontakt', k.telefon);
  };

  const handleSave = async () => {
    if (!form.tytul.trim()) { setError(t('calendar.title_field') + ' ' + t('common.error').toLowerCase()); return; }
    if (!form.data_od) { setError(t('calendar.dateFrom') + ' ' + t('common.error').toLowerCase()); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        ...form,
        klient_id: form.klient_id ? Number(form.klient_id) : null,
        data_do: form.data_do || null,
      };
      await onSave(payload);
      onClose();
    } catch(e) { setError(e.message); setSaving(false); }
  };

  const handleDelete = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setSaving(true);
    try { await onDelete(); onClose(); }
    catch(e) { setError(e.message); setSaving(false); }
  };

  const labelStyle = {
    display: 'block', fontSize: '9px', fontWeight: 600,
    letterSpacing: '0.07em', textTransform: 'uppercase',
    color: 'var(--ink3)', marginBottom: '5px',
  };

  return (
    <Modal
      title={isEdit ? t('calendar.edit') : t('calendar.add')}
      onClose={onClose}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

        {/* Typ */}
        <div>
          <label style={labelStyle}>{t('calendar.type')}</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {Object.entries(TYPY).filter(([k]) => k !== 'spacer').map(([k, v]) => {
              const Icon = v.Icon;
              return (
                <button
                  key={k}
                  onClick={() => set('typ', k)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '5px',
                    padding: '5px 10px',
                    borderRadius: '6px',
                    border: `1px solid ${form.typ === k ? v.color : 'var(--border)'}`,
                    background: form.typ === k ? v.bg : 'transparent',
                    color: form.typ === k ? v.color : 'var(--ink3)',
                    fontSize: '10px', fontWeight: 600, cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  <Icon className="w-3.5 h-3.5" /> {t(`calendar.types.${k}`)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tytuł */}
        <div>
          <label style={labelStyle}>{t('calendar.title_field')} *</label>
          <input
            className="ds-input"
            value={form.tytul}
            onChange={e => set('tytul', e.target.value)}
            placeholder={t('calendar.title_field')}
          />
        </div>

        {/* Daty */}
        <div className="form-row-2">
          <div>
            <label style={labelStyle}>{t('calendar.dateFrom')} *</label>
            <input
              className="ds-input"
              type="datetime-local"
              value={form.data_od}
              onChange={e => set('data_od', e.target.value)}
            />
          </div>
          <div>
            <label style={labelStyle}>{t('calendar.dateTo')}</label>
            <input
              className="ds-input"
              type="datetime-local"
              value={form.data_do}
              onChange={e => set('data_do', e.target.value)}
            />
          </div>
        </div>

        {/* Klient */}
        <div>
          <label style={labelStyle}>{t('calendar.client')}</label>
          <select
            className="ds-input"
            value={form.klient_id}
            onChange={e => handleKlientChange(e.target.value)}
          >
            <option value="">— {t('calendar.client')} —</option>
            {klienci.map(k => (
              <option key={k.id} value={k.id}>{k.nazwa}</option>
            ))}
          </select>
        </div>

        {/* Firma + Osoba + Telefon */}
        <div>
          <label style={labelStyle}>{t('calendar.contactCompany')}</label>
          <input
            className="ds-input"
            value={form.firma_kontakt}
            onChange={e => set('firma_kontakt', e.target.value)}
            placeholder="np. Kowalski Nieruchomości"
          />
        </div>

        <div className="form-row-2">
          <div>
            <label style={labelStyle}>{t('calendar.contactPerson')}</label>
            <input
              className="ds-input"
              value={form.osoba_kontakt}
              onChange={e => set('osoba_kontakt', e.target.value)}
              placeholder="np. Jan Kowalski"
            />
          </div>
          <div>
            <label style={labelStyle}>{t('calendar.contactPhone')}</label>
            <input
              className="ds-input"
              type="tel"
              value={form.telefon_kontakt}
              onChange={e => {
                const raw = e.target.value.replace(/\s/g, '');
                let fmt = raw;
                if (/^\+48\d*/.test(raw)) {
                  const digits = raw.slice(3);
                  fmt = '+48' + (digits.length ? ' ' : '') + digits.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
                } else if (/^\d{9,}/.test(raw)) {
                  fmt = raw.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
                }
                set('telefon_kontakt', fmt);
              }}
              placeholder="+48 600 000 000"
            />
          </div>
        </div>

        {/* Adres */}
        <div>
          <label style={labelStyle}>{t('calendar.address')}</label>
          <input
            className="ds-input"
            value={form.adres}
            onChange={e => set('adres', e.target.value)}
            placeholder={t('calendar.address')}
          />
        </div>

        {/* Notatki */}
        <div>
          <label style={labelStyle}>{t('calendar.notes')}</label>
          <textarea
            className="ds-input"
            rows={3}
            style={{ resize: 'none' }}
            value={form.notatki}
            onChange={e => set('notatki', e.target.value)}
            placeholder={t('calendar.notes')}
          />
        </div>

        {error && (
          <p style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--err2)' }}>
            <IconX className="w-3 h-3" /> {error}
          </p>
        )}

        <div style={{ display: 'flex', gap: '8px', paddingTop: '4px' }}>
          <Btn variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? '...' : t('common.save')}
          </Btn>
          <Btn variant="ghost" onClick={onClose}>{t('common.cancel')}</Btn>
          {isEdit && (
            <div style={{ marginLeft: 'auto' }}>
              <Btn variant="danger" onClick={handleDelete} disabled={saving}>
                {confirmDelete ? t('common.yes') + '?' : t('calendar.delete')}
              </Btn>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ─── EventChip ────────────────────────────────────────────────────────────────

function EventChip({ ev, onClick, compact }) {
  const typ = TYPY[ev._typ] || TYPY.inne;
  const Icon = typ.Icon;
  return (
    <div
      onClick={e => { e.stopPropagation(); onClick(ev); }}
      title={ev.tytul}
      style={{
        display: 'flex', alignItems: 'center', gap: '4px',
        background: typ.bg,
        border: `1px solid ${typ.color}`,
        borderLeft: `3px solid ${typ.color}`,
        borderRadius: '4px',
        padding: compact ? '1px 5px' : '3px 6px',
        fontSize: compact ? '9px' : '10px',
        color: 'var(--ink)',
        cursor: ev._spacer ? 'default' : 'pointer',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        marginBottom: '2px',
        transition: 'opacity 0.1s',
      }}
      onMouseEnter={e => !ev._spacer && (e.currentTarget.style.opacity = '0.8')}
      onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
    >
      <Icon className={compact ? 'w-2.5 h-2.5 flex-shrink-0' : 'w-3 h-3 flex-shrink-0'} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {compact ? ev.tytul : `${formatTime(ev.data_od) ? formatTime(ev.data_od) + ' ' : ''}${ev.tytul}`}
      </span>
    </div>
  );
}

// ─── ViewMiesiac ─────────────────────────────────────────────────────────────

function ViewMiesiac({ events, currentDate, onNavigate, onDayClick, onEventClick, t }) {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const days = t('daysShort', { returnObjects: true });

  // Build grid: days from prev month + this month + next month
  const firstDay = new Date(year, month, 1);
  const startDow = firstDay.getDay(); // 0=Sun
  const gridStart = addDays(firstDay, startDow === 0 ? -6 : 1 - startDow);
  const todayStr = dateToLocalISO(new Date());

  const cells = [];
  for (let i = 0; i < 42; i++) {
    cells.push(addDays(gridStart, i));
  }

  // Group events by date
  const byDate = {};
  events.forEach(ev => {
    const d = toDateOnly(ev.data_od);
    if (!byDate[d]) byDate[d] = [];
    byDate[d].push(ev);
  });

  const monthNames = t('months', { returnObjects: true });

  return (
    <div>
      {/* Nav */}
      <div className="flex items-center gap-2 sm:gap-3 mb-4 flex-wrap">
        <Btn variant="ghost" onClick={() => onNavigate(-1)}>‹</Btn>
        <span style={{ fontWeight: 700, fontSize: '14px', color: 'var(--ink)', textAlign: 'center' }} className="flex-1 sm:flex-none sm:min-w-[180px]">
          {monthNames[month]} {year}
        </span>
        <Btn variant="ghost" onClick={() => onNavigate(1)}>›</Btn>
        <Btn variant="ghost" onClick={() => onNavigate(0)}>{t('calendar.today')}</Btn>
      </div>

      {/* Day headers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: '2px', marginBottom: '4px' }}>
        {days.map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: '9px', fontWeight: 700, letterSpacing: '0.06em', color: 'var(--ink3)', padding: '4px 0' }}>
            {d}
          </div>
        ))}
      </div>

      {/* Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: '2px' }}>
        {cells.map((cell, i) => {
          const cellStr = dateToLocalISO(cell);
          const isCurrentMonth = cell.getMonth() === month;
          const isToday = cellStr === todayStr;
          const dayEvents = byDate[cellStr] || [];

          return (
            <div
              key={i}
              onClick={() => onDayClick(cell)}
              className="min-h-[56px] sm:min-h-[88px]"
              style={{
                background: isToday ? 'rgba(184,84,40,0.08)' : isCurrentMonth ? 'var(--bg3)' : 'var(--bg2)',
                border: isToday ? '1px solid var(--acc)' : '1px solid var(--border)',
                borderRadius: '6px',
                padding: '5px',
                cursor: 'pointer',
                transition: 'background 0.1s',
                minWidth: 0,
                overflow: 'hidden',
              }}
              onMouseEnter={e => { if (!isToday) e.currentTarget.style.background = 'var(--bg4)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = isToday ? 'rgba(184,84,40,0.08)' : isCurrentMonth ? 'var(--bg3)' : 'var(--bg2)'; }}
            >
              <div style={{
                fontSize: '10px', fontWeight: isToday ? 800 : 500,
                color: isToday ? 'var(--acc2)' : isCurrentMonth ? 'var(--ink2)' : 'var(--ink3)',
                marginBottom: '4px',
              }}>
                {cell.getDate()}
              </div>
              {dayEvents.slice(0, 3).map(ev => (
                <EventChip key={ev._id} ev={ev} onClick={onEventClick} compact />
              ))}
              {dayEvents.length > 3 && (
                <div style={{ fontSize: '9px', color: 'var(--ink3)', paddingLeft: '4px' }}>
                  +{dayEvents.length - 3}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── ViewTydzien ─────────────────────────────────────────────────────────────

function ViewTydzien({ events, currentDate, onNavigate, onEventClick, t }) {
  const days = t('daysShort', { returnObjects: true });
  const weekStart = isoWeekStart(currentDate);
  const todayStr = dateToLocalISO(new Date());

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const byDate = {};
  events.forEach(ev => {
    const d = toDateOnly(ev.data_od);
    if (!byDate[d]) byDate[d] = [];
    byDate[d].push(ev);
  });

  const monthNames = t('monthsShort', { returnObjects: true });

  const rangeLabel = `${weekDays[0].getDate()} ${monthNames[weekDays[0].getMonth()]} — ${weekDays[6].getDate()} ${monthNames[weekDays[6].getMonth()]} ${weekDays[6].getFullYear()}`;

  return (
    <div>
      {/* Nav */}
      <div className="flex items-center gap-2 sm:gap-3 mb-4 flex-wrap">
        <Btn variant="ghost" onClick={() => onNavigate(-1)}>‹</Btn>
        <span className="flex-1 sm:flex-none text-center" style={{ fontWeight: 700, fontSize: '13px', color: 'var(--ink)' }}>
          {rangeLabel}
        </span>
        <Btn variant="ghost" onClick={() => onNavigate(1)}>›</Btn>
        <Btn variant="ghost" onClick={() => onNavigate(0)}>{t('calendar.today')}</Btn>
      </div>

      {/* Columns */}
      <div className="grid grid-cols-1 sm:grid-cols-7 gap-2">
        {weekDays.map((day, i) => {
          const dayStr = dateToLocalISO(day);
          const isToday = dayStr === todayStr;
          const dayEvents = byDate[dayStr] || [];

          return (
            <div key={i} className="min-h-[60px] sm:min-h-[120px]" style={{
              background: isToday ? 'rgba(184,84,40,0.08)' : 'var(--bg3)',
              border: isToday ? '1px solid var(--acc)' : '1px solid var(--border)',
              borderRadius: '8px',
              padding: '8px',
            }}>
              <div style={{ marginBottom: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '9px', fontWeight: 700, letterSpacing: '0.07em', color: 'var(--ink3)' }}>
                  {days[i]}
                </div>
                <div style={{ fontSize: '16px', fontWeight: isToday ? 800 : 500, color: isToday ? 'var(--acc2)' : 'var(--ink)' }}>
                  {day.getDate()}
                </div>
              </div>
              {dayEvents.map(ev => (
                <EventChip key={ev._id} ev={ev} onClick={onEventClick} compact={false} />
              ))}
              {dayEvents.length === 0 && (
                <div style={{ fontSize: '9px', color: 'var(--ink3)', textAlign: 'center', marginTop: '8px' }}>
                  —
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── ViewLista ────────────────────────────────────────────────────────────────

function ViewLista({ events, t, onEventClick, onAddClick }) {
  const monthNames = t('months', { returnObjects: true });

  const todayStr = dateToLocalISO(new Date());
  const pastCutoff = dateToLocalISO(addDays(new Date(), -7));
  const futureCutoff = dateToLocalISO(addDays(new Date(), 60));

  const filtered = events.filter(ev => {
    const d = toDateOnly(ev.data_od);
    return d >= pastCutoff && d <= futureCutoff;
  });

  // Group by date
  const groups = {};
  filtered.forEach(ev => {
    const d = toDateOnly(ev.data_od);
    if (!groups[d]) groups[d] = [];
    groups[d].push(ev);
  });

  const sortedDates = Object.keys(groups).sort();

  if (sortedDates.length === 0) {
    return (
      <EmptyState
        icon={<IconCalendar className="w-12 h-12" />}
        title={t('calendar.noEvents')}
        description={t('calendar.noEventsHint')}
        action={onAddClick && (
          <Btn onClick={onAddClick}>
            <span className="flex items-center gap-1.5"><IconPlus className="w-3.5 h-3.5" /> {t('calendar.add')}</span>
          </Btn>
        )}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {sortedDates.map(dateStr => {
        const d = new Date(dateStr + 'T12:00:00');
        const isToday = dateStr === todayStr;
        const isPast = dateStr < todayStr;
        const dayLabel = `${d.getDate()} ${monthNames[d.getMonth()]} ${d.getFullYear()}`;

        return (
          <div key={dateStr}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px',
            }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                fontSize: '10px', fontWeight: 700,
                color: isToday ? 'var(--acc2)' : isPast ? 'var(--ink3)' : 'var(--ink2)',
                whiteSpace: 'nowrap',
              }}>
                {isToday && <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: 'var(--acc2)', display: 'inline-block' }} />}
                {dayLabel}
              </span>
              <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', opacity: isPast ? 0.65 : 1 }}>
              {groups[dateStr].map(ev => {
                const typ = TYPY[ev._typ] || TYPY.inne;
                return (
                  <div
                    key={ev._id}
                    onClick={() => !ev._spacer && onEventClick(ev)}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: '12px',
                      background: 'var(--bg3)', border: `1px solid var(--border)`,
                      borderLeft: `3px solid ${typ.color}`,
                      borderRadius: '6px', padding: '10px 12px',
                      cursor: ev._spacer ? 'default' : 'pointer',
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={e => !ev._spacer && (e.currentTarget.style.background = 'var(--bg4)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'var(--bg3)')}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
                        <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {ev.tytul}
                        </span>
                        {ev._spacer && (
                          <span style={{ fontSize: '9px', color: 'var(--ink3)', padding: '1px 5px', background: 'var(--bg4)', borderRadius: '4px' }}>
                            spacer
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', fontSize: '10px', color: 'var(--ink3)' }}>
                        {formatTime(ev.data_od) && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <IconClock className="w-3 h-3" /> {formatTime(ev.data_od)}{ev.data_do && formatTime(ev.data_do) ? ` – ${formatTime(ev.data_do)}` : ''}
                          </span>
                        )}
                        {ev.klient_nazwa && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <IconUser className="w-3 h-3" /> {ev.klient_nazwa}
                          </span>
                        )}
                        {ev.adres && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <IconLocation className="w-3 h-3" /> {ev.adres}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main Kalendarz Component ─────────────────────────────────────────────────

export default function Kalendarz() {
  const { t } = useTranslation();

  const [viewMode, setViewMode] = useState('miesiac'); // 'miesiac' | 'tydzien' | 'lista'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]); // merged zdarzenia + spacery
  const [klienci, setKlienci] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [modal, setModal] = useState(null); // null | { zdarzenie: {...} | null, defaultDate: '' }

  // iCal export
  const [showIcal, setShowIcal] = useState(false);
  const [icalToken, setIcalToken] = useState(null);
  const [icalLoaded, setIcalLoaded] = useState(false);

  // API_BASE: w prod backend siedzi pod osobną domeną (api.scanpilots.com),
  // klient pod app.scanpilots.com — używamy tego samego REACT_APP_API_URL co w api.js.
  const API_BASE = process.env.REACT_APP_API_URL || window.location.origin;
  const icalUrl = icalToken ? `${API_BASE}/api/kalendarz/${icalToken}.ics` : '';

  useEffect(() => {
    api.getIcalToken().then(data => {
      setIcalToken(data.token || null);
      setIcalLoaded(true);
    }).catch(() => setIcalLoaded(true));
  }, []);

  const enableIcal  = async () => { try { const d = await api.generateIcalToken(); setIcalToken(d.token); } catch(e) {} };
  const disableIcal = async () => { try { await api.deleteIcalToken(); setIcalToken(null); } catch(e) {} };
  const copyIcalUrl = () => {
    navigator.clipboard.writeText(icalUrl).catch(() => {});
  };

  // ── Fetch data ──

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const now = new Date();
      const odDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
      const doDate = new Date(now.getFullYear(), now.getMonth() + 3, 0);
      const od = dateToLocalISO(odDate);
      const doStr = dateToLocalISO(doDate);

      const [zdarzenia, spacery, kl] = await Promise.all([
        api.getZdarzenia(od, doStr),
        api.getSpacery(),
        api.getKlienci(),
      ]);

      setKlienci(kl);

      // Normalize zdarzenia
      const evZ = zdarzenia.map(z => ({
        ...z,
        _id: `z-${z.id}`,
        _typ: z.typ,
        _spacer: false,
      }));

      // Normalize spacery as read-only events
      const evS = spacery.map(s => ({
        _id: `s-${s.id}`,
        _typ: 'spacer',
        _spacer: true,
        id: s.id,
        tytul: `${s.nazwa}${s.klient_nazwa ? ' — ' + s.klient_nazwa : ''}`,
        data_od: s.data_wykonania,
        data_do: null,
        klient_nazwa: s.klient_nazwa,
        adres: s.adres,
        notatki: null,
        typ: 'spacer',
      }));

      setEvents([...evZ, ...evS].sort((a, b) => a.data_od.localeCompare(b.data_od)));
    } catch(e) {
      console.error('Kalendarz fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Navigation ──

  const navigate = (dir) => {
    setCurrentDate(prev => {
      if (viewMode === 'miesiac') {
        if (dir === 0) return new Date();
        const d = new Date(prev);
        d.setMonth(d.getMonth() + dir);
        return d;
      } else if (viewMode === 'tydzien') {
        if (dir === 0) return new Date();
        return addDays(prev, dir * 7);
      }
      return prev;
    });
  };

  // ── Open modal ──

  const openAdd = (date) => {
    const defaultDate = date ? `${dateToLocalISO(date)}T09:00` : '';
    setModal({ zdarzenie: null, defaultDate });
  };

  const openEdit = (ev) => {
    if (ev._spacer) return;
    // Find the raw zdarzenie
    const rawId = ev.id;
    setModal({ zdarzenie: { ...ev, id: rawId }, defaultDate: '' });
  };

  const handleSave = async (payload) => {
    if (modal?.zdarzenie?.id) {
      await api.updateZdarzenie(modal.zdarzenie.id, payload);
    } else {
      const data = { ...payload };
      if (modal?.defaultDate && !data.data_od) data.data_od = modal.defaultDate;
      await api.addZdarzenie(data);
    }
    await fetchAll();
  };

  const handleDelete = async () => {
    await api.deleteZdarzenie(modal.zdarzenie.id);
    await fetchAll();
  };

  const handleDayClick = (date) => {
    openAdd(date);
  };

  // ── View mode buttons ──

  const viewBtnStyle = (mode) => ({
    padding: '5px 14px',
    borderRadius: '6px',
    border: `1px solid ${viewMode === mode ? 'var(--acc)' : 'var(--border)'}`,
    background: viewMode === mode ? 'rgba(184,84,40,0.15)' : 'var(--bg3)',
    color: viewMode === mode ? 'var(--acc2)' : 'var(--ink3)',
    fontSize: '10px', fontWeight: 700, cursor: 'pointer',
    letterSpacing: '0.04em', transition: 'all 0.15s',
  });

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: '18px', fontWeight: 800, color: 'var(--ink)', letterSpacing: '-0.01em', marginRight: 'auto' }}>
          {t('calendar.title')}
        </h1>

        {/* View switcher */}
        <div style={{ display: 'flex', gap: '4px', background: 'var(--bg)', borderRadius: '8px', padding: '3px', border: '1px solid var(--border)' }}>
          {['miesiac', 'tydzien', 'lista'].map(mode => (
            <button key={mode} onClick={() => setViewMode(mode)} style={viewBtnStyle(mode)}>
              {t(`calendar.${mode === 'miesiac' ? 'month' : mode === 'tydzien' ? 'week' : 'list'}`)}
            </button>
          ))}
        </div>

        <Btn variant="ghost" onClick={() => setShowIcal(s => !s)}>
          {t('calendar.icalExport')}
        </Btn>
        <HelpHint text={t('help.icalExport')} label={t('calendar.icalExport')} />

        <Btn variant="primary" onClick={() => openAdd(null)}>
          + {t('calendar.add')}
        </Btn>
      </div>

      {/* ── iCal export panel ── */}
      {showIcal && icalLoaded && (
        <div className="ds-card" style={{ padding: '16px 20px', marginBottom: '16px' }}>
          <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--ink)', marginBottom: '4px' }}>
            {t('calendar.icalExport')}
          </p>
          <p style={{ fontSize: '10px', color: 'var(--ink3)', marginBottom: '12px' }}>
            {t('calendar.icalSubscribe')}
          </p>
          {icalToken ? (
            <>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <input
                  readOnly
                  value={icalUrl}
                  className="ds-input"
                  style={{ fontSize: '10px', flex: 1, minWidth: '240px' }}
                  onClick={e => e.target.select()}
                />
                <Btn variant="ghost" onClick={copyIcalUrl}>{t('common.copy')}</Btn>
                <Btn variant="danger" onClick={disableIcal}>{t('common.disable')}</Btn>
              </div>
            </>
          ) : (
            <Btn variant="ghost" onClick={enableIcal}>{t('calendar.icalGenerate')}</Btn>
          )}
        </div>
      )}

      {/* ── Content ── */}
      {loading ? (
        <div className="ds-card" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <Skeleton w="40px" h="36px" radius="6px" />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <Skeleton w="60%" h="12px" />
                <Skeleton w="40%" h="10px" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
          {viewMode === 'miesiac' && (
            <ViewMiesiac
              events={events}
              currentDate={currentDate}
              onNavigate={navigate}
              onDayClick={handleDayClick}
              onEventClick={openEdit}
              t={t}
            />
          )}
          {viewMode === 'tydzien' && (
            <ViewTydzien
              events={events}
              currentDate={currentDate}
              onNavigate={navigate}
              onEventClick={openEdit}
              t={t}
            />
          )}
          {viewMode === 'lista' && (
            <ViewLista
              events={events}
              t={t}
              onEventClick={openEdit}
              onAddClick={() => openAdd(null)}
            />
          )}
        </>
      )}

      {/* ── Modal ── */}
      {modal !== null && (
        <ZdarzenieModal
          zdarzenie={modal.zdarzenie
            ? modal.zdarzenie
            : modal.defaultDate
              ? { data_od: modal.defaultDate }
              : null
          }
          klienci={klienci}
          onSave={handleSave}
          onDelete={handleDelete}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
